/**
 * What the item artwork panel offers on a repeatable item's artwork, and how
 * urgently it needs a person (brandhub-order-routing 13 §5.1–§5.3). Headless:
 * the sentences are the read's (`validity.sentence`, a revision's
 * `status_text`); this only picks the tone and the offers.
 */

import type {
  RepeatableItemArtworkView,
  StaffProof,
} from "../data/artwork-staff-types";
import { currentTime } from "./current-time";

export type ItemArtworkTone = "neutral" | "success" | "warning" | "error";

/** Days ahead of valid until that the panel starts warning (the review sweep's default lead). */
export const REVIEW_LEAD_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The tone of the item's artwork today: orders stop on expired or undated; due soon or not approved warns. */
export function itemArtworkTone(
  view: RepeatableItemArtworkView,
  today: Date = currentTime(),
): ItemArtworkTone {
  const { status, valid_until: validUntil } = view.validity;
  if (status === "expired" || status === "undated") return "error";
  if (status === "not_approved") return "warning";
  if (status === "none") return "neutral";
  if (validUntil) {
    const daysLeft = (Date.parse(validUntil) - today.getTime()) / DAY_MS;
    if (daysLeft <= REVIEW_LEAD_DAYS) return "warning";
  }
  return "success";
}

/** Whether the item has a current approved revision (to confirm, and whose old stock a supply must decide). */
export function hasCurrentRevision(view: RepeatableItemArtworkView): boolean {
  return (
    view.validity.revision_no !== null &&
    ["valid", "expired", "undated"].includes(view.validity.status)
  );
}

/** Proofs of revisions supplied after the current one, waiting for staff to approve them for the customer. */
export function proofsAwaitingApproval(
  view: RepeatableItemArtworkView,
): StaffProof[] {
  return view.proofs.filter((p) => p.state === "out" && p.decide_url !== null);
}
